import { Injectable } from "@angular/core";
import { Observable } from 'rxjs';
import { Contenido, Revista, ContenidoStatus, Beneficio, BeneficioCategoria, Salutacion, Documento, BeneficioDetalle } from 'src/common/contenido';
import { HttpClient, HttpParams } from "@angular/common/http";
import { PageQuery } from 'src/common/page-query';
import { environment } from 'src/environments/environment';
import { SeccionesBranch } from "src/common/contenidoSeccionTypes";


@Injectable()
export class ContenidoService {

	private readonly BASE_URL: string = environment.backendUrl + '/contenido';
	private readonly DESTACADO_URL: string = this.BASE_URL + '/destacado';
	private readonly GENERAL_URL: string = this.BASE_URL + '/general';
	private readonly DETALLE_URL: string = this.BASE_URL + '/detalle';
	private readonly REVISTA_URL: string = this.BASE_URL + '/revista';
	private readonly BENEFICIO_URL: string = this.BASE_URL + '/beneficio';
	private readonly SALUTACION_URL: string = this.BASE_URL + '/salutacion';
	private readonly BENEFICIO_DETALLE_URL: string = this.BASE_URL + '/beneficio/detalle';
	private readonly FIND_GENERAL_STATUS_URL: string = this.BASE_URL + '/general/status';
	private readonly FIND_DESTACADO_STATUS_URL: string = this.BASE_URL + '/destacado/status';
	private readonly FIND_REVISTA_STATUS_URL: string = this.BASE_URL + '/revista/status';
	private readonly FIND_BENEFICIO_STATUS_URL: string = this.BASE_URL + '/beneficio/status';
	private readonly FIND_ALL_BENEFICIO_CATEGORIA_URL: string = this.BASE_URL + '/beneficio/categoria';
	private readonly FIND_SALUTACION_STATUS_URL: string = this.BASE_URL + '/salutacion/status';
	private readonly FIND_SECCIONES_BRANCH_URL: string = this.BASE_URL + '/seccion/branch';
	private readonly FIND_RECURSO_URL: string = this.BASE_URL + '/recurso';
	private readonly FIND_RECURSO_STATUS_URL: string = this.BASE_URL + '/recurso/status';
	private readonly FIND_REPORTE_EMERGENCIA_URL: string = this.BASE_URL + '/reporte/emergencia/detalle';

	private readonly PAGE_NUM_PARAM: string = 'pageNum';
	private readonly PAGE_SIZE_PARAM: string = 'pageSize';
	private readonly CATEGORIA_ID: string = 'categoriaId';
	private readonly ROOT_ABREV_PARAM: string = 'rootAbrev';
	private readonly DEPTH_PARAM: string = 'depth';
	private readonly SECCION_ABREV_PARAM: string = 'seccionAbrev';

	
	constructor( private http: HttpClient ) {
	}

	findDestacadoByPage( page: PageQuery ): Observable<Contenido[]> {
		let params: HttpParams = new HttpParams()
			.set( this.PAGE_NUM_PARAM, page.pageNum.toString() )
			.set( this.PAGE_SIZE_PARAM, page.pageSize.toString() );
		return this.http.get<Contenido[]>( this.DESTACADO_URL, { params } );
	}

	findGeneralByPage( page: PageQuery ): Observable<Contenido[]> {
		let params: HttpParams = new HttpParams()
			.set( this.PAGE_NUM_PARAM, page.pageNum.toString() )
			.set( this.PAGE_SIZE_PARAM, page.pageSize.toString() );
		return this.http.get<Contenido[]>( this.GENERAL_URL, { params } );
	}

	findContenidoDetalleByRid( contenidoRid: string ): Observable<Contenido> {
		let params: HttpParams = new HttpParams().set( 'rid', contenidoRid );
		return this.http.get<Contenido>( this.DETALLE_URL, { params } );
	}

	findRevistaByPage( page: PageQuery ): Observable<Revista[]> {
		let params: HttpParams = new HttpParams()
			.set( this.PAGE_NUM_PARAM, page.pageNum.toString() )
			.set( this.PAGE_SIZE_PARAM, page.pageSize.toString() );
		return this.http.get<Revista[]>( this.REVISTA_URL, { params } );
	}

	findBeneficioByPage( categoria: BeneficioCategoria, page: PageQuery ): Observable<Beneficio[]> {
		let params: HttpParams = new HttpParams()
			.set( this.CATEGORIA_ID, categoria.id.toString() )
			.set( this.PAGE_NUM_PARAM, page.pageNum.toString() )
			.set( this.PAGE_SIZE_PARAM, page.pageSize.toString() );
		return this.http.get<Beneficio[]>( this.BENEFICIO_URL, { params } );
	}

	findSalutacionByPage( page: PageQuery ): Observable<Salutacion[]> {
		let params: HttpParams = new HttpParams()
			.set( this.PAGE_NUM_PARAM, page.pageNum.toString() )
			.set( this.PAGE_SIZE_PARAM, page.pageSize.toString() );
		return this.http.get<Salutacion[]>( this.SALUTACION_URL, { params } );
	}

	findBeneficioDetalleByRid( beneficioRid: string ): Observable<BeneficioDetalle> {
		let params: HttpParams = new HttpParams().set( 'rid', beneficioRid );
		return this.http.get<BeneficioDetalle>( this.BENEFICIO_DETALLE_URL, { params } );
	}

	findGeneralStatus(): Observable<ContenidoStatus> {
		return this.http.get<ContenidoStatus>( this.FIND_GENERAL_STATUS_URL );
	}

	findDestacadoStatus(): Observable<ContenidoStatus> {
		return this.http.get<ContenidoStatus>( this.FIND_DESTACADO_STATUS_URL );
	}

	findRevistaStatus(): Observable<ContenidoStatus> {
		return this.http.get<ContenidoStatus>( this.FIND_REVISTA_STATUS_URL );
	}

	findBeneficioStatus( categoria: BeneficioCategoria ): Observable<ContenidoStatus> {
		let params: HttpParams = new HttpParams().set( this.CATEGORIA_ID, categoria.id.toString() );
		return this.http.get<ContenidoStatus>( this.FIND_BENEFICIO_STATUS_URL, { params } );
	}

	findAllBeneficioCategorias(): Observable<BeneficioCategoria[]> {
		return this.http.get<BeneficioCategoria[]>( this.FIND_ALL_BENEFICIO_CATEGORIA_URL );
	}

	findSalutacionStatus(): Observable<ContenidoStatus> {
		return this.http.get<ContenidoStatus>( this.FIND_SALUTACION_STATUS_URL );
	}

	findSeccionesBranch( rootAbrev: string, depth: number ): Observable<SeccionesBranch> {
		let params: HttpParams = new HttpParams()
			.set( this.ROOT_ABREV_PARAM, rootAbrev )
			.set( this.DEPTH_PARAM, depth.toString() );
		return this.http.get<SeccionesBranch>( this.FIND_SECCIONES_BRANCH_URL, { params } );
	}

	findRecursoByPage( seccionAbrev: string, page: PageQuery ): Observable<Documento[]> {
		let params: HttpParams = new HttpParams()
			.set( this.SECCION_ABREV_PARAM, seccionAbrev )
			.set( this.PAGE_NUM_PARAM, page.pageNum.toString() )
			.set( this.PAGE_SIZE_PARAM, page.pageSize.toString() );
		return this.http.get<Documento[]>( this.FIND_RECURSO_URL, { params } );
	}

	findRecursoStatus( seccionAbrev: string ): Observable<ContenidoStatus> {
		let params: HttpParams = new HttpParams().set( this.SECCION_ABREV_PARAM, seccionAbrev )
		return this.http.get<ContenidoStatus>( this.FIND_RECURSO_STATUS_URL, { params } );
	}

	findReporteEmergenciaDetalle(): Observable<Contenido> {
		return this.http.get<Contenido>( this.FIND_REPORTE_EMERGENCIA_URL );
	}

}